import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type IconPrefix } from '@fortawesome/fontawesome-svg-core';

import { CaseType, HomeScoreCategory } from '~/common/enums';

import { type FileTypeKey, isFileType } from '../utils/fileHelpers';

import addressIcon from '../img/address/undrraw_address_blue.svg';
import bronzeBoostedIcon from '../img/contractors/bronze_star.svg';
import bronzeIcon from '../img/contractors/bronze.svg';
import buildingIcon from '../img/buildings/undraw_building_blue.svg';
import goldBoostedIcon from '../img/contractors/gold_star.svg';
import goldIcon from '../img/contractors/gold.svg';
import notApplicableBoostedIcon from '../img/contractors/not_applicable_star.svg';
import notApplicableIcon from '../img/contractors/not_applicable.svg';
import policyIcon from '../img/policy/undraw_policy_blue.svg';
import silverBoostedIcon from '../img/contractors/silver_star.svg';
import silverIcon from '../img/contractors/silver.svg';

type IconProps = {
  [key: string]: unknown;
};

type StyledIconProps = IconProps & {
  iconStyle?: IconPrefix;
};

export const AdminIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'screwdriver-wrench']} {...props} />
);

export const AdministratorIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'user-crown']} {...props} />
);

export const HomeIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'house']} {...props} />
);

export const AutomaticIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'gears']} {...props} />
);

export const SettingsIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'gear']} {...props} />
);

export const ManualIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'hand']} {...props} />
);

export const MailboxIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'inbox-full']} {...props} />
);

export const CalendarIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'calendar-days']} {...props} />
);

export const CalendarDayIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'calendar-day']} {...rest} />
);

export const DueDateIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'calendar-circle-exclamation']} {...props} />
);

export const ReminderDateIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'calendar-clock']} {...props} />
);

export const CircleExclamation: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon {...rest} icon={[iconStyle ?? 'fas', 'circle-exclamation']} />
);
export const PlayIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'play']} {...props} />
);

export const PauseIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'pause']} {...props} />
);

export const ClockIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'clock']} {...props} />
);

export const ReminderReadyIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'bolt']} {...props} />
);

export const LoaderIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'loader']} {...props} />
);

export const ContactIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'address-book']} {...props} />
);

export const CompanyIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'industry-windows']} {...props} />
);

export const ValidCompanyIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'shield-check']} {...props} />
);

export const InsurancePolicyIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'file-contract']} {...props} />
);

export const BuildingIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'house-building']} {...props} />
);

export const FilterIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'filter']} {...props} />
);

export const ClearFilterIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'filter-circle-xmark']} {...props} />
);

export const CaseIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'box-archive']} {...props} />
);

export const InsuredCaseIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'umbrella']} {...props} />
);

export const RepairCaseIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'screwdriver-wrench']} {...props} />
);

export const ComplaintCaseIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'message-exclamation']} {...props} />
);

export const MarketplaceIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'shopping-cart']} {...props} />
);

export const IntegrationIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'link-simple']} {...props} />
);

export const ReportIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'chart-line']} {...props} />
);

export const SearchIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'magnifying-glass']} {...props} />
);

export const TeamIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'people-group']} {...props} />
);

export const DeleteIcon: React.FC<StyledIconProps> = ({ iconStyle, ...props }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'trash']} {...props} />
);

export const DeleteIconAlt: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'trash-alt']} {...props} />
);

export const EditIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'pencil']} {...rest} />
);

export const SaveIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'save']} {...props} />
);

export const UndoIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'undo']} {...props} />
);

export const RedoIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'redo']} {...props} />
);

export const AcceptIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'check-circle']} {...props} />
);

export const CancelIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'circle-xmark']} {...props} />
);

export const CircleIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['far', 'circle']} {...props} />
);

export const FullCircleIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'circle']} {...props} />
);

export const TimerIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['far', 'timer']} {...props} />
);

export const PreviewIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'file-magnifying-glass']} {...props} />
);

export const EyeIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'eye']} {...rest} />
);

export const OpenIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'arrow-right-to-arc']} {...props} />
);

export const NewTabIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'arrow-up-right-from-square']} {...props} />
);

export const UpdateIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'arrow-right-arrow-left']} {...props} />
);

export const AddIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'plus']} {...rest} />
);

export const KeyIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'key']} {...props} />
);

export const LogoutIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'arrow-right-from-bracket']} {...props} />
);
export const DivisionKeyIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'split']} {...props} />
);

export const RepeatIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'repeat']} {...props} />
);

export const ExcelIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'file-excel']} {...props} />
);

export const CsvIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'file-csv']} {...props} />
);

export const PowerpointIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'file-powerpoint']} {...props} />
);

export const UserIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'user']} {...props} />
);

export const UsersIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'users']} {...props} />
);

export const DropDownIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'chevron-down']} {...props} />
);

export const SettleIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'money-bill']} {...props} />
);

export const EuroIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'euro-sign']} {...props} />
);

export const EuroCircleIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'circle-euro']} {...props} />
);

export const DropDownIconUp: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'chevron-up']} {...props} />
);

export const TaskIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'flag-pennant']} {...props} />
);

export const SnoozeIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'alarm-snooze']} {...props} />
);

export const PriorityIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'arrows-from-dotted-line']} {...props} />
);

export const PriorityLowIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'down-from-dotted-line']} {...props} />
);

export const PriorityNormalIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'horizontal-rule']} {...props} />
);

export const PriorityHighIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'up-from-dotted-line']} {...props} />
);

export const BlogIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'rss']} {...props} />
);

export const LogsIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'clipboard-list']} {...props} />
);

export const AddressIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'location-dot']} {...props} />
);

export const DownloadIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'download']} {...props} />
);

export const UploadIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'upload']} {...props} />
);

export const ValidIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'circle-check']} {...props} />
);

export const ValidIconSolid: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'circle-check']} {...props} />
);

export const InvalidIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'circle-xmark']} {...props} />
);

export const NeutralIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'circle-minus']} {...props} />
);

export const ToValidateIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'stamp']} {...props} />
);

export const RejectedIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'x']} {...props} />
);

export const ImportIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'cloud-arrow-up']} {...props} />
);

export const EmailIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'envelope']} {...rest} />
);

export const LetterIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'mailbox']} {...rest} />
);

export const SmsIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'message']} {...rest} />
);

export const MessageQuestionIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'message-question']} {...props} />
);

export const PhoneIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'phone']} {...props} />
);

export const InboundPhoneCall: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'phone-arrow-down-left']} {...props} />
);

export const OutBoundPhoneCall: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'phone-arrow-up-right']} {...props} />
);

export const DebtorsIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'sack-dollar']} {...props} />
);

export const ProvisionsIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'hand-holding-dollar']} {...props} />
);

export const MobilePhoneIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'mobile']} {...props} />
);

export const InboundIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'inbox-in']} {...props} />
);

export const OutboundIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'inbox-out']} {...props} />
);

export const InsuranceIntermediaryIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'umbrella']} {...props} />
);

export const InsurerIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'fire-extinguisher']} {...props} />
);

export const InsuranceEngineerIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'briefcase']} {...props} />
);

export const SpreadsheetIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'file-spreadsheet']} {...props} />
);

export const PropertyManagerIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'gavel']} {...props} />
);

export const HomeOwnerAssociationIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'building-user']} {...props} />
);

export const PermissionsIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'shield-check']} {...props} />
);

export const InfoIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'circle-info']} {...rest} />
);

export const FinanceIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'coins']} {...props} />
);

export const CompletedIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'check']} {...props} />
);

export const LinkIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'link']} {...props} />
);

export const UnlinkIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'link-slash']} {...props} />
);

export const ChartPieIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'chart-pie']} {...props} />
);

export const FemaleIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'venus']} {...props} />
);
export const MaleIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'mars']} {...props} />
);
export const UnknownGenderIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'mars-and-venus']} {...props} />
);

export const AssignIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'person-arrow-down-to-line']} {...props} />
);

export const RiskObjectGuaranteeIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'badge-check']} {...props} />
);

export const SkipIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'arrow-right-long-to-line']} {...props} />
);

export const ClearIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'xmark']} {...rest} />
);

export const LoadingIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon spin icon={['fad', 'spinner-third']} {...props} />
);

export const PreviousIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'arrow-left']} {...props} />
);

export const BackIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'arrow-left']} {...props} />
);

export const NextIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'arrow-right']} {...props} />
);

export const FlagIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['far', 'flag']} {...props} />
);

export const RefreshIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'arrows-rotate']} {...props} />
);

export const SyncIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'arrows-rotate']} {...props} />
);

export const SyncingIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon spin icon={['fad', 'arrows-rotate']} {...props} />
);

export const IndentationArrowIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['far', 'arrow-turn-down-right']} {...props} />
);

export const NoteIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'sticky-note']} {...rest} />
);

export const WarningIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'triangle-exclamation']} {...rest} />
);

export const SendIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'paper-plane']} {...props} />
);

export const SendArrowIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'paper-plane-top']} {...props} />
);

export const SwapIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'rotate-reverse']} {...props} />
);

export const AngleUpIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'angle-up']} {...props} />
);

export const AngleDownIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'angle-down']} {...props} />
);

export const AngleRightIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'angle-right']} {...props} />
);
export const AngleLeftIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'angle-left']} {...props} />
);

export const AccessibilityIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'eye']} {...rest} />
);

export const CommentIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'comment']} {...rest} />
);

export const BuildingIconPretty = () => (
  <img alt="building-icon" className="block h-16 w-16" src={buildingIcon} />
);

export const AddressIconPretty = () => (
  <img alt="address-icon" className="block h-16 w-16" src={addressIcon} />
);

export const InsurancePolicyIconPretty = () => (
  <img alt="policy-icon" className="block h-16 w-16" src={policyIcon} />
);

export const BanIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'ban']} {...props} />
);

export const GlobeIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'globe']} {...props} />
);

export const PinIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon="thumbtack" {...props} />
);

export const ThumbsUpIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'thumbs-up']} {...props} />
);

export const ThumbsDownIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'thumbs-down']} {...props} />
);

export const FileSignatureIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'file-signature']} {...props} />
);

export const FileIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'file']} {...rest} />
);

export const EllipsisIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon="ellipsis" {...props} />
);

export const EllipsisIconVertical: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'ellipsis-vertical']} {...rest} />
);

export const FolderTreeIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'folder-tree']} {...props} />
);

export const FoldersIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'folders']} {...rest} />
);

export const FolderIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'folder']} {...rest} />
);

export const ListIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'list']} {...props} />
);

export const LockIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'lock']} {...props} />
);

export const ClassificationIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'tags']} {...props} />
);

export const ResponsibilityIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'user-police-tie']} {...props} />
);

export const ColumnsIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'eye']} {...props} />
);

export const ColumnsIconSlash: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'eye-slash']} {...props} />
);

export const HandshakeIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'handshake']} {...props} />
);

export const SignatureIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'signature']} {...props} />
);

export const PiggyBankIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'piggy-bank']} {...props} />
);
export const MergeIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'layer-group']} {...props} />
);

export const MoreFiltersIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'diagram-next']} {...props} />
);

export const HideFiltersIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'diagram-previous']} {...props} />
);

export const InlineSpinner: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'circle-notch']} {...props} spin />
);

export const LanguageIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'language']} {...rest} />
);

export const IbanBicIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'credit-card-front']} {...props} />
);

export const PaymentPeriodIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'money-check-dollar-pen']} {...props} />
);

export const MeetingIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'calendar-lines-pen']} {...props} />
);

export const ReminderIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'bell']} {...props} />
);

export const SupportIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'user-headset']} {...props} />
);

export const FsmaIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'input-numeric']} {...props} />
);

export const QrIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'qrcode']} {...props} />
);

export const CopyIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'copy']} {...props} />
);

export const MenuIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'bars']} {...props} />
);

export const ProcurationIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'book-section']} {...props} />
);

export const ImageIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'image']} {...props} />
);

export const ImageDocumentIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'file-image']} {...props} />
);

export const ReceiptIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'receipt']} {...props} />
);

export const InvoiceIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'file-invoice']} {...props} />
);

export const AbstainIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'xmark-to-slot']} {...props} />
);

export const IndividualDivisionIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'people']} {...props} />
);

export const DividerIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'pipe']} {...props} />
);

export const AttachmentsIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'paperclip']} {...rest} />
);

export const ChevronsDownIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'chevrons-down']} {...props} />
);

export const WelcomeIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'hand-wave']} {...props} />
);

export const ColumnGridIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'columns-3']} {...props} />
);

export const CashRegisterIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'cash-register']} {...props} />
);

export const MaximizeIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'expand']} {...props} />
);

export const MinimizeIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'window-minimize']} {...props} />
);

export const GripIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'grip-lines']} {...props} />
);

export const ChevronLeftIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'chevron-left']} {...props} />
);

export const ChevronRightIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'chevron-right']} {...props} />
);

export const ZoomInIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'magnifying-glass-plus']} {...props} />
);

export const ZoomOutIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'magnifying-glass-minus']} {...props} />
);

export const CenterIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'arrows-to-circle']} {...props} />
);

export const OrderIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'arrow-down-arrow-up']} {...rest} />
);

export const BarFilterIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'bars-filter']} {...rest} />
);

export const CheckboxIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'square-check']} {...props} />
);

export const SelectAllIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'check-double']} {...props} />
);

export const CurrencyIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'square-dollar']} {...props} />
);

export const DocumentIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'file-lines']} {...rest} />
);

export const BalanceSheetIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'scale-balanced']} {...props} />
);

export const BudgetConceptIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'ballot-check']} {...props} />
);

export const EmailIconAlt: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'at']} {...props} />
);

export const NumericInputIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'input-numeric']} {...props} />
);

export const VotingDeviceIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'calculator']} {...props} />
);

export const TextIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'text']} {...props} />
);

export const UploadIconAlt: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'arrow-up-from-bracket']} {...props} />
);

export const EmptyIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'empty-set']} {...props} />
);

export const CheckIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon {...rest} icon={[iconStyle ?? 'fas', 'check']} />
);

export const PdfIcon: React.FC<StyledIconProps> = ({ iconStyle, ...props }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fas', 'file-pdf']} {...props} />
);

export const WordIcon: React.FC<StyledIconProps> = ({ iconStyle, ...props }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fas', 'file-word']} {...props} />
);

export const StarIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fas', 'star']} {...rest} />
);

export const RoundingIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'arrow-down-up-across-line']} {...props} />
);

export const ArrowUpIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'arrow-up']} {...props} />
);

export const ArrowDownIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'arrow-down']} {...props} />
);

export const ZipIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'file-zipper']} {...props} />
);

export const NotApplicableIcon: React.FC<IconProps> = (props) => (
  <img alt="not-applicable-icon" src={notApplicableIcon} {...props} />
);

export const GoldIcon: React.FC<IconProps> = (props) => (
  <img alt="gold-icon" src={goldIcon} {...props} />
);

export const SilverIcon: React.FC<IconProps> = (props) => (
  <img alt="silver-icon" src={silverIcon} {...props} />
);

export const BronzeIcon: React.FC<IconProps> = (props) => (
  <img alt="bronze-icon" src={bronzeIcon} {...props} />
);

export const NotApplicableBoostedIcon: React.FC<IconProps> = (props) => (
  <img alt="not-applicable-boosted-icon" src={notApplicableBoostedIcon} {...props} />
);

export const GoldBoostedIcon: React.FC<IconProps> = (props) => (
  <img alt="gold-boosted-icon" src={goldBoostedIcon} {...props} />
);

export const SilverBoostedIcon: React.FC<IconProps> = (props) => (
  <img alt="silver-boosted-icon" src={silverBoostedIcon} {...props} />
);

export const BronzeBoostedIcon: React.FC<IconProps> = (props) => (
  <img alt="bronze-boosted-icon" src={bronzeBoostedIcon} {...props} />
);

export const DirectPaymentIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'money-check-pen']} {...props} />
);

export const AccountancyIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'calculator-simple']} {...props} />
);

export const ExportIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'file-export']} {...props} />
);

export const SwitchCameraIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fas', 'camera-rotate']} {...props} />
);

export const GraphIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'chart-mixed']} {...props} />
);

export const AccountIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'money-check']} {...props} />
);

export const WaitingIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'hourglass-clock']} {...props} />
);

export const CalculatorIcon: React.FC<IconProps> = (props) => (
  <FontAwesomeIcon icon={['fad', 'calculator']} {...props} />
);

export const HomeScoreCategoryIcon: React.FC<{
  category: HomeScoreCategory;
  className?: string;
  isBoosted?: boolean;
}> = ({ category, isBoosted, ...rest }) => {
  const homeScoreCategoryIconMap = {
    [HomeScoreCategory.NotApplicable]: <NotApplicableIcon {...rest} />,
    [HomeScoreCategory.Gold]: <GoldIcon {...rest} />,
    [HomeScoreCategory.Silver]: <SilverIcon {...rest} />,
    [HomeScoreCategory.Bronze]: <BronzeIcon {...rest} />,
  };

  const homeScoreCategoryBoostedIconMap = {
    [HomeScoreCategory.NotApplicable]: <NotApplicableBoostedIcon {...rest} />,
    [HomeScoreCategory.Gold]: <GoldBoostedIcon {...rest} />,
    [HomeScoreCategory.Silver]: <SilverBoostedIcon {...rest} />,
    [HomeScoreCategory.Bronze]: <BronzeBoostedIcon {...rest} />,
  };

  return (
    (isBoosted
      ? homeScoreCategoryBoostedIconMap[category]
      : homeScoreCategoryIconMap[category]) ?? <NotApplicableIcon {...rest} />
  );
};

export const GripDotsIcon: React.FC<IconProps & { orientation: 'horizontal' | 'vertical' }> = ({
  orientation,
  ...props
}) => (
  <FontAwesomeIcon
    icon={['fas', `grip-dots${orientation === 'vertical' ? '-vertical' : ''}`]}
    {...props}
  />
);

export const PercentageIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fas', 'percent']} {...props} />
);

export const BookYearIcon: React.FC<IconProps> = () => (
  <FontAwesomeIcon icon={['fas', 'book']} size="lg" />
);

export const TransferBookYearIcon: React.FC<IconProps> = () => (
  <span className="fa-layers fa-fw">
    <FontAwesomeIcon icon={['fas', 'book']} size="lg" />
    <FontAwesomeIcon
      color="lightblue"
      icon={['fas', 'right-left']}
      transform="shrink- up-8 right-6"
    />
  </span>
);

export const MemoCircleIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fas', 'memo-circle-info']} {...props} />
);

export const MergeArrowIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fas', 'merge']} {...props} />
);

export const DescriptionsIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fas', 'subtitles']} {...props} />
);

export const ReplyIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'reply']} {...rest} />
);

export const ForwardIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'share']} {...rest} />
);

export const AccessKeyManagementIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fas', 'key-skeleton-left-right']} {...props} />
);

export const DivisionAccessKeyIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fas', 'key-skeleton']} {...props} />
);

export const AccessKeyIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fad', 'key']} {...props} />
);

export const SupplierIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fas', 'boxes-stacked']} {...props} />
);

export const ArchiveIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fas', 'archive']} {...props} />
);

export const CodaSuspenseAccountIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fas', 'user-clock']} {...props} />
);

export const BoardMembersIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fas', 'users']} {...props} />
);

export const BookingRulesIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fas', 'book-open']} {...props} />
);

export const CostReallocationIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fas', 'sitemap']} {...props} />
);

export const ShareIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fas', 'share-nodes']} {...props} />
);

export const GeneralLedgerIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fas', 'book']} {...props} />
);

export const SalesCaseIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'file-invoice-dollar']} {...rest} />
);

export const StopIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'stop']} {...rest} />
);

export const VotingBallotIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'ballot']} {...rest} />
);

export const PeriodIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fad', 'arrows-left-right']} {...props} />
);

export const CaseTypeIcon: React.FC<IconProps & { caseType: CaseType }> = ({
  caseType,
  ...props
}) => {
  const caseTypeIconMap = {
    [CaseType.Case]: <CaseIcon {...props} />,
    [CaseType.Complaint]: <ComplaintCaseIcon {...props} />,
    [CaseType.InsuranceClaim]: <InsuredCaseIcon {...props} />,
    [CaseType.Repair]: <RepairCaseIcon {...props} />,
    [CaseType.Sales]: <SalesCaseIcon {...props} />,
  };

  return caseTypeIconMap[caseType];
};

const CONTENT_TYPE_ICONS = [
  {
    Icon: ImageDocumentIcon,
    typeKey: 'img',
  },
  {
    Icon: ExcelIcon,
    typeKey: 'ms-excel',
  },
  {
    Icon: PowerpointIcon,
    typeKey: 'ms-powerpoint',
  },
  {
    Icon: WordIcon,
    typeKey: 'ms-word',
  },
  {
    Icon: PdfIcon,
    typeKey: 'pdf',
  },
  {
    Icon: TextIcon,
    typeKey: 'txt',
  },
] as const satisfies {
  Icon: React.FC<IconProps>;
  typeKey: FileTypeKey;
}[];

export const ContentTypeIcon: React.FC<IconProps & { contentType: string }> = ({
  contentType,
  ...props
}) => {
  const Icon =
    CONTENT_TYPE_ICONS.find((icon) => isFileType(icon.typeKey, contentType))?.Icon ?? FileIcon;

  return <Icon {...props} iconStyle="fad" />;
};

export const CreditIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fad', 'file-invoice-dollar']} {...props} />
);

export const PrintIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fad', 'print']} {...props} />
);

export const TransferIcon: React.FC<IconProps> = ({ ...props }) => (
  <FontAwesomeIcon icon={['fad', 'right-left']} {...props} />
);

export const AiIcon: React.FC<StyledIconProps> = ({ iconStyle, ...rest }) => (
  <FontAwesomeIcon icon={[iconStyle ?? 'fad', 'sparkles']} {...rest} />
);